// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TippyPopupStyle } from "@turnerBlueprint/App/ComponentExtensions/Tippy/TippyPopupExtensions";
import HeaderClickable from "@turnerBlueprint/App/Components/Header/HeaderClickable";
import TippyPopup from "@turnerBlueprint/App/Components/Tippy/TippyPopup";
import { getIsChatDisabled } from "@turnerBlueprint/App/Services/ChatService";
import { CustomAppState } from "@turnerBlueprint/App/Store/Reducers";
import { xIsAuthenticated } from "@turnerBlueprint/App/Store/Selectors/GeneralSelectors";
import IconChat from "@turnerBlueprint/XDL/Icons/IconChat";
import IconFooterMessage from "@turnerBlueprint/XDL/Icons/IconFooterMessage";
import IconFooterPhone from "@turnerBlueprint/XDL/Icons/IconFooterPhone";
import IconHeaderSupport from "@turnerBlueprint/XDL/Icons/IconHeaderSupport";
import { triggerEsc } from "@turnerBlueprint/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { withTheme } from "styled-components";

const enum fields {
    phone = "phone",
    hours = "hours",
    email = "email",
    emailDisplay = "emailDisplay",
    showChat = "showChat",
    contactUsUrl = "contactUsUrl",
}

const mapStateToProps = (state: CustomAppState) => ({
    isTabletScreen: state.xCommon.isTabletScreen,
});

export interface HeaderSupportStyles {
    tippyPopup?: TippyPopupStyle;
    wrapper?: InjectableCss;
    title?: TypographyProps;
    row?: InjectableCss;
    icon?: IconProps;
    link?: LinkPresentationProps;
    hours?: TypographyProps;
}

export const headerSupportStyles: HeaderSupportStyles = {};

interface OwnProps extends WidgetProps {
    theme: BaseTheme;
    fields: {
        [fields.phone]: string;
        [fields.hours]: string;
        [fields.email]: string;
        [fields.emailDisplay]: string;
        [fields.showChat]: boolean;
        [fields.contactUsUrl]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;
const styles = headerSupportStyles;

export const HeaderSupportComponent = ({ fields, showTitle }: any) => {
    const [showChat, setShowChat] = useState(false);

    const { isAuthenticated, isChatDisabled } = useAppSelector(state => ({
        isAuthenticated: xIsAuthenticated(state),
        isChatDisabled: (state as CustomAppState).xCommon.isChatDisabled,
    }));

    const dispatch = useDispatch();

    React.useEffect(() => {
        (async () => {
            if (isAuthenticated) {
                const isDisabled = await getIsChatDisabled();
                dispatch({ type: "xCommon/update", data: { key: "isChatDisabled", value: isDisabled } });
            }
        })();
    }, [isAuthenticated]);

    React.useEffect(() => {
        setShowChat(fields.showChat && !isChatDisabled);
    }, [isChatDisabled]);

    return (
        <StyledWrapper {...styles.wrapper}>
            {showTitle && <Typography {...styles.title}>{translate("Contact Us")}</Typography>}
            {showChat && (
                <StyledWrapper {...styles.row}>
                    <Icon src={IconChat} {...styles.icon} />
                    <Link
                        {...styles.link}
                        onClick={() => {
                            hideAll();
                            (window as any)?.SnapEngage?.isMobile()
                                ? document.getElementById("snapengage-button")?.click()
                                : (window as any)?.SnapEngage?.startChat();
                        }}
                    >
                        {translate("Live Chat")}
                    </Link>
                </StyledWrapper>
            )}
            {!!fields.phone && (
                <StyledWrapper {...styles.row}>
                    <Icon src={IconFooterPhone} {...styles.icon} />
                    <Link {...styles.link} href={`tel:${fields.phone}`}>
                        {translate("Call")} {fields.phone}
                        {!!fields.hours && <Typography {...styles.hours}>{fields.hours}</Typography>}
                    </Link>
                </StyledWrapper>
            )}
            {!!fields.email && (
                <StyledWrapper {...styles.row}>
                    <Icon src={IconFooterMessage} {...styles.icon} />
                    <Link {...styles.link} href={`mailto:${fields.email}`}>
                        {fields.emailDisplay || fields.email}
                    </Link>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
const HeaderSupport: FC<Props> = ({ isTabletScreen, fields }) => {
    const [isPopupShown, setIsPopupShown] = React.useState(false);

    const history = useHistory();
    const openContactUsPageHandler = () => {
        if (fields.contactUsUrl) {
            history.push(fields.contactUsUrl);
        }
    };

    if (isTabletScreen) {
        return null;
    }
    return (
        <TippyPopup
            extendedStyles={styles.tippyPopup}
            maxWidth={204}
            showCloseButton={false}
            content={<HeaderSupportComponent fields={fields} showTitle={true} />}
            arrow={false}
            offset={BLUEPRINT_NAME === "turnerBlueprint" ? [0, 19] : [0, 22]}
            placement="bottom-end"
            type="menu"
            onTriggerPopupOpen={isOpen => setIsPopupShown(isOpen)}
            onShow={instance => {
                hideAll();
                triggerEsc();
            }}
        >
            <HeaderClickable
                iconProps={{
                    src: IconHeaderSupport,
                }}
                data-test-selector="supportLink"
                title={translate("Support")}
                isOpen={isPopupShown}
                onClick={openContactUsPageHandler}
            />
        </TippyPopup>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withTheme(HeaderSupport)),
    definition: {
        displayName: "Header Support",
        fieldDefinitions: [
            {
                name: fields.phone,
                displayName: "Support Phone",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.hours,
                displayName: "Working HOurs",
                editorTemplate: "TextField",
                defaultValue: "Mon-Fri 8am – 5pm ET",
                fieldType: "General",
            },
            {
                name: fields.email,
                displayName: "Support Email",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.emailDisplay,
                displayName: "Support Email Display",
                editorTemplate: "TextField",
                defaultValue: "Email Us",
                fieldType: "General",
            },
            {
                name: fields.showChat,
                displayName: "Show Chat Link",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.contactUsUrl,
                displayName: "Contact Us Url",
                editorTemplate: "TextField",
                defaultValue: "ContactUs",
                fieldType: "General",
            },
        ],
        group: "Common",
        allowedContexts: ["Header"],
    },
};

export default widgetModule;
